import {
  HStack,
  InputGroup,
  InputLeftElement,
  Select,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { SearchIcon } from "../../components/icons";
import { MainContainer } from "../../components/mainContainer";
import { FilterBar } from "../../components/FilterBar";
import * as Font from "../../components/texts";
import { MenuActionButton } from "../../components/menuActionButton";
import { useCallback, useEffect, useState } from "react";
import { MenuPopUp } from "../../components/menuPopUp";
import { Pagination } from "../../components/pagination";
import * as T from "../../components/table";
import * as Inputs from "../../components/inputs";
import { RegisterModal } from "./RegisterModal";
import { useClient } from "@/hooks/useClient";
import { ClientIndexDto, ClientIndexFilterDto, ClientStoreDto } from "./types";
import LoadingOverlay from "@/components/overlay";
import { maskCPF } from "@/utils/masks";
import { useParams } from "react-router-dom";
import { SubmitHandler } from "react-hook-form";
import { useStore } from "@/hooks/useStore";
import { useCustomToast } from "@/hooks/useToast";

const initialFilter: ClientIndexFilterDto = {
  codeOrNameOrClass: "",
  situation: "todos",
};

/* eslint-disable react/no-children-prop */
export const Clients = () => {
  const { storeId } = useParams();
  const [showMenuPopUp, setShowMenuPopUp] = useState<undefined | number>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { customToast } = useCustomToast();
  const { getStoreName } = useStore();
  const { getClients, clientStore, clientSituationUpdate, clientImport } =
    useClient();
  const [page, setPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [storeName, setStoreName] = useState<string | undefined>();
  const [clients, setClients] = useState<ClientIndexDto[]>([]);
  const [totalClients, setTotalClients] = useState<number>(0);
  const [lastPage, setLastPage] = useState<number>(1);
  const [filter, setFilter] = useState<ClientIndexFilterDto>(initialFilter);
  const [stopedTyping, setStopedTyping] = useState(false);
  const [typingTimer, setTypingTimer] = useState<NodeJS.Timeout | undefined>();

  useEffect(() => {
    getStoreName(Number(storeId))
      .then((response) => setStoreName(response.data.name))
      .catch((error) =>
        customToast("error", [error.response.data.message].join(", "))
      );
  }, []);

  const handleInputChange = () => {
    clearTimeout(typingTimer);

    const newTypingTimer = setTimeout(() => {
      setStopedTyping(true);
    }, 500);

    setTypingTimer(newTypingTimer);
  };

  const filters = [
    {
      field: (
        <InputGroup alignItems="center" key={1}>
          <InputLeftElement
            pointerEvents="none"
            children={<SearchIcon />}
            top=""
            left={1}
          />
          <Inputs.SimpleText
            placeholder="Buscar por ID, nome ou Tipo"
            w="100%"
            h={9}
            borderRadius={4}
            textIndent="35px"
            bg="neutral.0"
            onChange={(event) => {
              setFilter({ ...filter, codeOrNameOrClass: event.target.value });
              handleInputChange();
            }}
          />
        </InputGroup>
      ),
    },
    {
      field: (
        <HStack w={260} justifyContent="flex-end" key={2}>
          <Text
            w={100}
            fontFamily="Inter"
            fontStyle="normal"
            fontWeight="400"
            fontSize="14px"
            lineHeight="17px"
            color="neutral.500"
          >
            Filtrar por status:
          </Text>
          <Select
            width={110}
            h={9}
            borderRadius={4}
            bg="neutral.0"
            onChange={(event) =>
              setFilter({ ...filter, situation: event.target.value })
            }
          >
            <option value="">Todos</option>
            <option value="ativos">Ativos</option>
            <option value="inativos">Inativos</option>
          </Select>
        </HStack>
      ),
    },
  ];

  const callbackClients = useCallback(() => {
    setIsLoading(true);

    getClients({ page, storeId: Number(storeId), ...filter })
      .then((response) => {
        setClients(response.data.data);
        setTotalClients(response.data.total);
        setLastPage(response.data.lastPage);
      })
      .catch((error) =>
        customToast("error", [error.response.data.message].join(", "))
      )
      .finally(() => setIsLoading(false));
  }, [page, filter]);

  useEffect(() => callbackClients(), [page, filter.situation]);
  useEffect(() => {
    if (stopedTyping) {
      if (page === 1) {
        callbackClients();
      } else {
        setPage(1);
      }
      setStopedTyping(false);
    }
  }, [filter.codeOrNameOrClass, stopedTyping]);

  const onSubmit: SubmitHandler<ClientStoreDto> = (props) => {
    const data: ClientStoreDto = {
      ...props,
      situation: props.situation ? true : false,
    };
    setIsLoading(true);

    clientStore(Number(storeId), data)
      .then(() => {
        customToast("success", "Cadastrado com sucesso");
        callbackClients();
        onClose();
      })
      .catch((error) =>
        customToast("error", [error.response.data.message].join(", "))
      )
      .finally(() => setIsLoading(false));
  };

  const onImportFile = (file: any) => {
    setIsLoading(true);

    clientImport(Number(storeId), file)
      .then(() => {
        customToast("success", "Importado com sucesso");
        callbackClients();
      })
      .catch((error) =>
        customToast("error", [error.response.data.message].join(", "))
      )
      .finally(() => setIsLoading(false));
  };

  return (
    <MainContainer
      height={isLoading || clients.length < 10 ? undefined : "100%"}
      title={`Eventos > ${storeName} > Clientes`}
    >
      <FilterBar
        title={`Clientes cadastrados (${totalClients})`}
        subTitle="Lista de clientes do evento"
        buttons={[
          {
            title: "Adicionar cliente",
            onClick: onOpen,
          },
          {
            title: "Importar planilha",
            onClick: onImportFile,
            type: "inputFile",
          },
        ]}
        filters={filters}
      />
      {isLoading && <LoadingOverlay />}
      <Stack w="100%" p={5} mt="0 !important">
        <TableContainer>
          <Table w="100%">
            <Thead>
              <Tr>
                <Th textAlign="center">
                  <Font.TableTheadText>Código</Font.TableTheadText>
                  <Font.TableTheadText>Cliente</Font.TableTheadText>
                </Th>
                <Th textAlign="center">
                  <Font.TableTheadText>Nome</Font.TableTheadText>
                </Th>
                <Th textAlign="center">
                  <Font.TableTheadText>CPF</Font.TableTheadText>
                </Th>
                <Th textAlign="center">
                  <Font.TableTheadText>Tipo</Font.TableTheadText>
                </Th>
                <Th textAlign="center">
                  <Font.TableTheadText>Status</Font.TableTheadText>
                </Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {clients.map((client) => (
                <T.Body key={client.id}>
                  <Td textAlign="center">
                    <Font.TableBodyText>{client.code}</Font.TableBodyText>
                  </Td>
                  <Td textAlign="center">
                    <Font.TableBodyText>{client.name}</Font.TableBodyText>
                  </Td>
                  <Td textAlign="center">
                    <Font.TableBodyText>
                      {maskCPF(client.cpf)}
                    </Font.TableBodyText>
                  </Td>
                  <Td textAlign="center">
                    <Font.TableBodyText>{client.class}</Font.TableBodyText>
                  </Td>
                  <Td textAlign="center">
                    <Font.TableBodyText>
                      {client.situation ? "Ativo" : "Inativo"}
                    </Font.TableBodyText>
                  </Td>
                  <Td>
                    <MenuActionButton
                      color="#2F2F2F"
                      onClick={() => setShowMenuPopUp(client.id)}
                      _hover={{
                        bgColor: "neutral.200",
                      }}
                      _active={{
                        bgColor: "neutral.300",
                      }}
                    />
                  </Td>
                  {showMenuPopUp === client.id && (
                    <MenuPopUp
                      top="-4px"
                      right="36px"
                      options={[
                        {
                          title: client.situation ? "Inativar" : "Ativar",
                          onClick: () => {
                            setIsLoading(true);
                            clientSituationUpdate(
                              client.id,
                              !client.situation
                            ).finally(() => callbackClients());
                          },
                        },
                      ]}
                      setShowMenuPopUp={setShowMenuPopUp}
                    />
                  )}
                </T.Body>
              ))}
            </Tbody>
            <Tfoot w="100%" h="15px"></Tfoot>
          </Table>
          <Pagination page={page} lastPage={lastPage} onClick={setPage} />
        </TableContainer>
        <RegisterModal isOpen={isOpen} onClose={onClose} onSubmit={onSubmit} />
      </Stack>
    </MainContainer>
  );
};
